/*
 * Copyright (c) 2020. American Express Company
 */
import * as _ from 'lodash';
import { LocalDate, LocalDateTime } from '@js-joda/core';
import DateUtils from 'src/utils/date';
import { AccountModel } from './account.model';
import { CardModel } from './card.model';
import { CashFlowModel } from './cash-flow.model';
import { DebtModel } from './debt.model';
import { InstitutionModel } from './institution.model';
import { InvestmentModel } from './investment.model';
import { RealEstateModel } from './real-estate.model';
import { InvestmentHoldingModel } from './investment-holding.model';
import TimeService from '../services/time.service';
import LiteralsService from '../services/literals/LiteralsService';
import { PersonModel } from 'src/types/person.model';
import { PlanModel } from 'src/types/plan.model';

export class Summary {
    sum: number;
    name: string;
    month: number;
    year: number;
    id: number;
}

export enum StatusCode {
    PreStatus = 'your-life-plan', // need to get onboarded
    LotsOCash = 'lots-o-cash', // $10mm at age 65, look no further
    OutOfCash = 'out-of-cash', // run out of cash
    TooLate = 'too-late', // won't hit 25x expenses before retirement
    Never = 'never', // will never hit 25x expenses
    OnTrack = 'on-track', // will hit 25x expenses by retirement
    OffTrack = 'off-track'
}

export enum PlanType {
    BUDGETING = 'budgeting',
    FULL = 'full'
}

export interface FullUserModel {
    planType: PlanType.FULL;
    id: string;
    cashFlow: CashFlowModel[];
    rawCashFlow: CashFlowModel[];
    dob: LocalDate;
    email: string;
    forename: string;
    surname: string;
    cards: CardModel[];
    institutions: InstitutionModel[];
    accounts: AccountModel[];
    debts: DebtModel[];
    holdings: InvestmentHoldingModel[];
    realEstate: RealEstateModel[];
    investments: InvestmentModel[];
    independenceDay: LocalDate | string;
    status: StatusCode;
    dangerZoneStart?: LocalDate;
    oauth?: string;
    avatar: string;
    summaries: Summary[];
    peopleWithPhotos: PersonModel[];
    /**
     * If the user is a temporary user (mostly only affects backend, skips some data syncing and email alerts if true).
     * This should only be true on demo/test users.
     */
    temporary?: boolean;
    snapshotId?: string;
    lastDataUpdate: string;
    created: string;
    lastLogin?: string;
    currentNetWorth: number;
    refreshToken?: string;
    expenseTransactionsToReviewCount: number;
    incomeTransactionsToReviewCount: number;
    attributes: {
        [key: string]: any;
    };

    termsVersion?: number;
    termsUpdatedAt?: LocalDateTime;
    defaultPlan: PlanModel;
    currentScenarioUid?: string;
    defaultScenarioUid?: string;

    [key: string]: any;
}

export interface BudgetUserModel {
    planType: PlanType.BUDGETING;
    id: string;
    forename: string;
    accounts: AccountModel[];
    attributes: {
        [key: string]: any;
    };

    created: string;
    currentScenarioUid?: string;
    defaultPlan: PlanModel;
    defaultScenarioUid?: string;
    lastLogin?: string;
    peopleWithPhotos: PersonModel[];
    temporary?: boolean;
    termsVersion?: number;
    institutions?: InstitutionModel[];
}

export type UserModel = FullUserModel | BudgetUserModel;

export class UserUtils {
    public static isBudgetingUser(user: UserModel): user is BudgetUserModel {
        return user && user.planType === PlanType.BUDGETING;
    }

    public static serialize(user: UserModel): string {
        return JSON.stringify(user);
    }

    public static deserializeDates<T extends UserModel>(user: T): T {
        if (user.planType === PlanType.FULL) {
            user.dob = DateUtils.dateify(user.dob);
            if (user.cashFlow) {
                user.cashFlow.forEach((cashFlow) => {
                    cashFlow.startDate = DateUtils.dateify(cashFlow.startDate);
                    cashFlow.endDate = DateUtils.dateify(cashFlow.endDate);
                });
            }
            if (user.rawCashFlow) {
                user.rawCashFlow.forEach((cashFlow) => {
                    cashFlow.startDate = DateUtils.dateify(cashFlow.startDate);
                    cashFlow.endDate = DateUtils.dateify(cashFlow.endDate);
                });
            }
            if (_.isString(user.independenceDay)) {
                // this might be "Never"
                if (user.independenceDay !== 'Never') {
                    user.independenceDay = DateUtils.dateify(user.independenceDay);
                }
            }
            if (_.isString(user.dangerZoneStart)) {
                user.dangerZoneStart = DateUtils.dateify(user.dangerZoneStart);
            }
            if (user.cards) {
                user.cards.forEach((card) => {
                    if (card) {
                        // not sure why this is sometimes null
                        card.startDate = DateUtils.dateify(card.startDate);
                        card.endDate = TimeService.minDate(
                            DateUtils.dateify(card.endDate),
                            user.cashFlow[user.cashFlow.length - 1].endDate
                        );
                    }
                });
            }
            if (user.investments) {
                user.investments.forEach((investment) => {
                    investment.startDate = DateUtils.dateify(investment.startDate);
                    investment.endDate = DateUtils.dateify(investment.endDate);
                });
            }
            if (user.institutions) {
                user.institutions.forEach((institution) => {
                    institution.created = DateUtils.datetimeify(institution.created);
                    institution.updated = DateUtils.datetimeify(institution.updated);
                });
            }
            if (user.holdings) {
                user.holdings.forEach((holding) => {
                    holding.created = DateUtils.datetimeify(holding.created);
                    holding.updated = DateUtils.datetimeify(holding.updated);
                });
            }
            if (user.debts) {
                user.debts.forEach((debt) => {
                    debt.startDate = DateUtils.dateify(debt.startDate);
                    debt.endDate = DateUtils.dateify(debt.endDate);
                    debt.created = DateUtils.datetimeify(debt.created);
                    debt.updated = DateUtils.datetimeify(debt.updated);
                });
            }
            if (user.realEstate) {
                user.realEstate.forEach((ra) => {
                    ra.mortgageDate = DateUtils.dateify(ra.mortgageDate);
                    ra.created = DateUtils.datetimeify(ra.created);
                    ra.updated = DateUtils.datetimeify(ra.updated);
                });
            }
            if (user.termsUpdatedAt) {
                user.termsUpdatedAt = DateUtils.datetimeify(user.termsUpdatedAt);
            }
        }

        if (user.peopleWithPhotos) {
            user.peopleWithPhotos.forEach((p) => {
                p.dob = DateUtils.dateify(p.dob);
            });
        }
        if (user.defaultPlan) {
            user.defaultPlan = PlanModel.deserializeDates(user.defaultPlan);
        }
        if (user.accounts) {
            user.accounts.forEach((account) => {
                account.created = DateUtils.datetimeify(account.created);
                account.updated = DateUtils.datetimeify(account.updated);
            });
        }

        return user;
    }

    public static getStatusMessage(userStatus: StatusCode): string {
        switch (userStatus) {
            case StatusCode.OffTrack:
                return LiteralsService.getLiteral('StatusCode.OffTrack');
            case StatusCode.OnTrack:
                return LiteralsService.getLiteral('StatusCode.OnTrack');
        }
    }
}
